import { redirect, Form, Link, useFetcher, useLoaderData } from "react-router";
import { motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { faEyeSlash } from "@insidebeehive/fortawesome/pro-solid-svg-icons/faEyeSlash";
import { faEye } from "@insidebeehive/fortawesome/pro-solid-svg-icons/faEye";
import { faXmark } from "@insidebeehive/fortawesome/pro-solid-svg-icons/faXmark";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";

import type { RecentAccount } from "~/utils/recent-accounts";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import type { AuthenticationCheck } from "~/models/interface/common.interface";

import { generateAndStoreDeviceKey, getStoredDeviceKey, exportDevicePublicKeyB64, signChallengeB64 } from "~/utils/device-key.client";
import { getRecentAccounts, removeRecentAccount } from "~/utils/recent-accounts";
import ErrorComponent from "~/components/common/error-component";
import { Page } from "~/components/custom/page";
import AnimatedGradientText from "~/components/ui/animated-gradient-text";
import { Button } from "~/components/ui/button";
import { Flex, FlexItem } from "~/components/ui/flex";
import { Icon } from "~/components/ui/icon";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "~/components/ui/otp-input";
import { useAppDispatch, useAppSelector } from "~/hook/use-redux";
import { useDeviceInfo } from "~/hook/use-device-info";
import { signInAction } from "~/models/services/auth.server";
import { useRemixForm } from "~/packages/remix-hook-form";
import { setExportStack } from "~/store/global";
import { authenticationCheck } from "~/utils/utils.server";
import { loginResolver } from "~/validations/login.validation";
import { CardContent, CardDescription, CardTitle } from "~/components/ui/card";
import { TEST_IDS } from "~/constants/test-ids";

export const handle = { i18n: ["bonus", "common", "table"] };

// @simplewebauthn/browser doesn't re-export its option JSON types from its public entry point
// (they live in the internal @simplewebauthn/types package, not a direct dependency here) —
// derive them from the functions we already import instead of adding an unresolvable import.
type PasskeyAuthOptionsJSON = Parameters<typeof startAuthentication>[0]["optionsJSON"];
type PasskeyRegOptionsJSON = Parameters<typeof startRegistration>[0]["optionsJSON"];

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const host = request.headers.get("x-forwarded-host") || request.headers.get("host") || "";
  const brandName = host.split(".")[1] || "bstudio";

  const authCheckResponse: AuthenticationCheck = await authenticationCheck(request);
  if (!authCheckResponse.authFlag) {
    return {
      bypass2fa: process.env.BYPASS2FA === "true",
      brandName,
    };
  }

  return redirect("/admin/dashboard");
};

export const action = async (args: ActionFunctionArgs) => await signInAction(args);

// Login steps: "accounts" → "email" → "password" (normal) or passkey auto-trigger
// "passkey-auth-cancelled": user cancelled the biometric prompt on an account with ACTIVE passkey —
// primary action is a retry of the same challenge. The only fallback is "register a new passkey on
// this device," which still forces a fresh password+2FA check server-side (see
// forceRegisterNewDevice) before minting registration options — a bare password bypass is not
// offered here.
// "passkey-register-cancelled": user cancelled the registration prompt — must retry registration.
// "passkey-register-conflict": startRegistration threw InvalidStateError — this authenticator
// (e.g. iCloud Keychain) already holds a resident credential for this exact (rpID, userHandle)
// pair, independent of what the server's excludeCredentials says. Retrying the same request can
// never succeed; the user must remove the stale entry from the authenticator itself.
// "passkey-unsupported": startAuthentication/startRegistration threw a device/config error
// (SecurityError, ConstraintError, NotSupportedError) — per SimpleWebAuthn's error identification
// (see identifyAuthenticationError.ts / identifyRegistrationError.ts), these mean a persistent
// rpID/origin misconfiguration or an authenticator capability mismatch, NOT a transient cancel.
// Retrying the identical request will fail identically every time.
// "device-pending": this is a brand-new device awaiting admin approval (server returned
// devicePending: true) — a terminal state with no retry loop; the user must wait for an admin.
// "device-revoked": an admin explicitly revoked/rejected this device (server returned
// deviceRevoked: true) — distinct from device-pending: nothing is "pending" here, an admin must
// reset the device or passkey before this browser/device can sign in again. The account's WebAuthn
// credential is untouched by a device revoke, so without this terminal state the passkey ceremony
// keeps succeeding on retry while the device gate blocks every attempt with a misleading message.
// "device-limit": this account has hit its allowed-device count (server returned
// deviceLimitReached: true) — distinct from both above: no admin action is pending or was taken
// against THIS device, it's simply that every slot is already occupied by other devices. The
// resolution is admin-side (free up a slot) rather than a retry, so this is a terminal state too.
type LoginStep = "accounts" | "email" | "password" | "passkey-auth-cancelled" | "passkey-register-cancelled" | "passkey-register-conflict" | "passkey-unsupported" | "device-pending" | "device-revoked" | "device-limit";

// The password step is reused for two distinct reasons: (1) needsPassword — user has no passkey at
// all, plain login; (2) forceRegisterNewDevice — user HAS a passkey credential elsewhere but this
// browser/device could never complete the auth ceremony (no local WebAuthn credential, not synced).
// The second case must still force a fresh password+2FA check server-side before minting new
// registration options — see the forceRegisterNewDevice flag in signInAction's initial-password
// branch — so this flag rides along on that same submit rather than skipping straight to
// registration.

// Errors SimpleWebAuthn surfaces for a persistent device/browser/config problem rather than a
// transient user action — see SimpleWebAuthn identifyAuthenticationError.ts /
// identifyRegistrationError.ts (native DOMException names re-thrown as WebAuthnError.name).
// ConstraintError covers three distinct causes as of v13 (missing discoverable-credential support,
// auto-register UV failure, missing UV support) — all share this same .name, so the broad check
// below already catches them; .code (captured separately in capturePasskeyError) is finer detail.
const NON_RETRYABLE_PASSKEY_ERRORS = new Set(["SecurityError", "ConstraintError", "NotSupportedError"]);
const isNonRetryablePasskeyError = (err: unknown): boolean => err instanceof Error && NON_RETRYABLE_PASSKEY_ERRORS.has(err.name);

// Report a WebAuthn ceremony failure with the SimpleWebAuthn-specific detail (.code)
// that a bare err.message would lose, plus which step of the login flow it happened in.
const capturePasskeyError = (err: unknown, context: { step: string; hasCtxToken: boolean }) => {
  console.error(err, {
    feature: "passkey-login",
    step: context.step,
    errorName: err instanceof Error ? err.name : typeof err,
    errorCode: (err as { code?: string } | undefined)?.code,
    hasCtxToken: context.hasCtxToken,
  });
};

// SimpleWebAuthn's own generate*Options default to a 60s ceremony timeout, enforced by the
// browser's navigator.credentials call — but on rare platform bugs that promise can hang forever
// (e.g. a broken authenticator driver never resolving user-verification). Race it against a
// slightly longer client-side ceiling so passkeyProcessing can never spin indefinitely with no
// escape route besides a manual page refresh.
const PASSKEY_CEREMONY_TIMEOUT_MS = 65_000;
class PasskeyTimeoutError extends Error {
  name = "PasskeyTimeoutError";
}
function withPasskeyTimeout<T>(promise: Promise<T>): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => reject(new PasskeyTimeoutError("Passkey ceremony timed out")), PASSKEY_CEREMONY_TIMEOUT_MS);
    }),
  ]);
}

// `Promise.race` does not cancel the losing promise — if the real WebAuthn ceremony resolves
// AFTER our timeout already fired (and the UI already moved to a different step), its .then()/
// .catch() would otherwise still fire and silently act on stale state (e.g. calling
// fetcher.submit() behind the user's back). A per-attempt generation counter lets every callback
// check "is this still the attempt that's current?" before touching state, without needing
// AbortController support from SimpleWebAuthn (it doesn't accept a signal).

const Login = () => {
  const deviceFingerPrint = useAppSelector((state) => state.global.deviceFingerPrint);
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const fetcher = useFetcher<typeof signInAction>();
  const isLoading = useMemo(() => fetcher.state === "loading" || fetcher.state === "submitting", [fetcher.state]);
  const dispatch = useAppDispatch();
  const [token, setToken] = useState("");
  const { bypass2fa, brandName } = useLoaderData<typeof loader>();
  const deviceInfo = useDeviceInfo();
  const [passkeyProcessing, setPasskeyProcessing] = useState(false);
  // Populated once the server tells us which userId is logging in (IndexedDB device keys are
  // scoped per-user, so we can't look one up before then), so the plain password-login submit
  // also carries it — otherwise the server's device-key gate can never recognize a returning
  // device on the password path and re-enrolls a fresh key every login.
  const [storedDevicePublicKey, setStoredDevicePublicKey] = useState<string>("");
  const knownUserIdRef = useRef<string>("");
  const passkeyHandled = useRef(false);
  const passkeyFlowComplete = useRef(false);
  const [loginStep, setLoginStep] = useState<LoginStep>("email");
  const [forceRegisterNewDevice, setForceRegisterNewDevice] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [recentAccounts, setRecentAccounts] = useState<RecentAccount[]>([]);
  const [selectedAccountEmail, setSelectedAccountEmail] = useState<string | null>(null);
  // Stored passkey options + context token for retry after auth/registration cancel.
  const pendingPasskeyAuthRef = useRef<{ options: PasskeyAuthOptionsJSON; ctxToken: string } | null>(null);
  const pendingPasskeyRegRef = useRef<{ options: PasskeyRegOptionsJSON; ctxToken: string } | null>(null);
  // Bumped every time a new passkey ceremony (auth/registration, initial or retry) starts.
  // Each ceremony's .then()/.catch() captures the generation at start time and checks it's still
  // current before acting — closes the race where a real ceremony resolves after our client-side
  // timeout already fired and moved the UI elsewhere (see withPasskeyTimeout above).
  const passkeyGenerationRef = useRef(0);

  const defaultValues: any = {
    email: "",
    password: "",
    deviceFingerPrint: "",
    token: "",
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    getValues,
    setValue,
  } = useRemixForm({
    mode: "onSubmit",
    resolver: loginResolver,
    defaultValues,
    submitData: {
      deviceFingerPrint,
      token,
      devicePlatform: deviceInfo.platform,
      devicePlatformVersion: deviceInfo.platformVersion,
      deviceBrowserName: deviceInfo.browserName,
      deviceBrowserVersion: deviceInfo.browserVersion,
      deviceIsMobile: String(deviceInfo.isMobile),
      ...(storedDevicePublicKey ? { devicePublicKey: storedDevicePublicKey } : {}),
      ...(forceRegisterNewDevice ? { forceRegisterNewDevice: "true" } : {}),
    },
    fetcher,
  });

  useEffect(() => {
    localStorage.setItem("exportsToastStack", JSON.stringify([]));
    dispatch(setExportStack([]));
  }, []);

  useEffect(() => {
    const accounts = getRecentAccounts();
    setRecentAccounts(accounts);
    if (accounts.length > 0) {
      setLoginStep("accounts");
    }
  }, []);

  // Step 1 handler: check email for passkey
  const handleEmailCheck = (e: React.FormEvent) => {
    e.preventDefault();
    const email = getValues("email");
    if (!email) {
      toast.error("Please enter your email");

      return;
    }
    setEmailValue(email);
    fetcher.submit(
      {
        email,
        _action: "check-email",
        deviceFingerPrint: deviceFingerPrint || "",
        devicePlatform: deviceInfo.platform,
        devicePlatformVersion: deviceInfo.platformVersion,
        deviceBrowserName: deviceInfo.browserName,
        deviceBrowserVersion: deviceInfo.browserVersion,
        deviceIsMobile: String(deviceInfo.isMobile),
      },
      { method: "post" },
    );
  };

  // Handle server responses
  useEffect(() => {
    const fetcherData = fetcher?.data as any;
    if (!fetcherData || passkeyHandled.current) return;

    // The server sends userId on every passkey/device-key response once it knows which user is
    // logging in. IndexedDB device keys are scoped per-user, so we can't look one up until this
    // is known — populate storedDevicePublicKey (for the plain password-login submitData) as soon
    // as we learn it, and remember it for every subsequent device-key call site in this effect.
    if (typeof fetcherData.userId === "string" && fetcherData.userId) {
      if (knownUserIdRef.current !== fetcherData.userId) {
        knownUserIdRef.current = fetcherData.userId;
        (async () => {
          const stored = await getStoredDeviceKey(fetcherData.userId).catch(() => null);
          const b64 = stored ? await exportDevicePublicKeyB64(stored.publicKey).catch(() => "") : "";
          setStoredDevicePublicKey(b64);
        })();
      }
    }

    // Email check response: needs password (no active passkey)
    if (fetcherData.needsPassword) {
      setLoginStep("password");

      return;
    }

    // New device is PENDING admin approval — terminal state, no retry loop. The server already
    // showed its toast; route to the dedicated waiting screen (not back into a retry step, whose
    // ctxToken is now consumed and would render a dead "Try Again" button). Reset 2FA token so a
    // later fresh attempt starts clean.
    if (fetcherData.devicePending) {
      setPasskeyProcessing(false);
      passkeyHandled.current = false;
      setToken("");
      setLoginStep("device-pending");

      return;
    }

    // Device explicitly revoked/rejected by an admin — terminal state, no retry loop. The
    // account's passkey credential is untouched by revoke, so retrying would just re-run the same
    // biometric ceremony successfully and hit this same gate again; route to a dedicated screen
    // instead of the generic toast + passkey-auth-cancelled retry loop.
    if (fetcherData.deviceRevoked) {
      setPasskeyProcessing(false);
      passkeyHandled.current = false;
      setToken("");
      // Defensively drop the revoked device's stored key so it can never be resubmitted on a
      // later attempt from this same effect (the server re-validates row status independently,
      // but this closes the client-side path too rather than relying only on that).
      setStoredDevicePublicKey("");
      knownUserIdRef.current = "";
      setLoginStep("device-revoked");

      return;
    }

    // Account has hit its device limit — terminal state, no retry loop. Unlike device-revoked,
    // no admin action was taken against this specific device; every slot is just already taken by
    // other devices, so the fix is admin-side (free a slot) rather than anything this browser can do.
    if (fetcherData.deviceLimitReached) {
      setPasskeyProcessing(false);
      passkeyHandled.current = false;
      setToken("");
      setLoginStep("device-limit");

      return;
    }

    // Server-side failure on a passkey flow — release every latch so the UI never gets stuck
    // spinning, regardless of which response shape the failure took (toast, redirect, etc.).
    if (fetcherData.toast && fetcherData.status === false) {
      passkeyHandled.current = false;
      passkeyFlowComplete.current = false;
      setPasskeyProcessing(false);
    }

    // Passkey authentication (from check-email or from normal login flow)
    if (fetcherData.passkeyAuth && fetcherData.passkeyOptions && typeof fetcherData.passkeyOptions === "object") {
      passkeyHandled.current = true;
      setPasskeyProcessing(true);
      const ctx = fetcherData.loginContext || {};
      const ctxToken: string | undefined = ctx.token;
      const generation = ++passkeyGenerationRef.current;
      withPasskeyTimeout(startAuthentication({ optionsJSON: fetcherData.passkeyOptions }))
        .then((authResponse) => {
          // Stale callback: a timeout (or a newer ceremony) already fired for this generation
          // and moved the UI on — the real ceremony resolving late must not act on state anymore.
          if (generation !== passkeyGenerationRef.current) return;
          // ctx.token (Redis-backed UUID) is the server-issued login context handle.
          // Without it, this is a check-email passkey prompt — server requires password.
          if (!ctxToken) {
            toast.error("Please enter your password to continue.");
            setPasskeyProcessing(false);
            passkeyHandled.current = false;
            setSelectedAccountEmail(null);
            setLoginStep("password");

            return;
          }
          passkeyFlowComplete.current = true;
          // Reset the handled-latch before submitting: it was set true above to suppress this
          // effect while startAuthentication() awaited, but the verify-passkey response (e.g.
          // deviceKeyEnrollRequired/deviceKeyChallenge/devicePending) must be processed by this
          // same effect on its next run — leaving it true here silently swallows that response.
          passkeyHandled.current = false;
          // Send the already-stored device public key (if any) so the server's device-key gate can
          // recognize a returning, already-ACTIVE device instead of treating every login as brand
          // new — without this the gate always sees no key and re-enrolls a fresh one every time,
          // creating a new UserDevice row and re-queuing it for admin approval on every login.
          (async () => {
            const userId = fetcherData.userId as string | undefined;
            const stored = userId ? await getStoredDeviceKey(userId).catch(() => null) : null;
            const devicePublicKey = stored ? await exportDevicePublicKeyB64(stored.publicKey).catch(() => undefined) : undefined;
            fetcher.submit(
              {
                loginContextToken: ctxToken,
                _action: "verify-passkey",
                passkeyResponse: JSON.stringify(authResponse),
                ...(devicePublicKey ? { devicePublicKey } : {}),
              },
              { method: "post" },
            );
          })();
        })
        .catch((err: unknown) => {
          const isTimeout = err instanceof PasskeyTimeoutError;
          // A stale non-timeout rejection (real ceremony finally failing after we already moved on
          // from a timeout) must not act on state. The timeout's OWN rejection must still run —
          // it's what bumps the generation and puts the UI into a recoverable state.
          if (!isTimeout && generation !== passkeyGenerationRef.current) return;
          if (isTimeout) {
            passkeyGenerationRef.current += 1;
          }
          setPasskeyProcessing(false);
          passkeyHandled.current = false;
          setSelectedAccountEmail(null);
          if (isTimeout) {
            toast.error("The biometric prompt took too long to respond. Please try again.");
          }
          if (!isTimeout && (!(err instanceof Error) || err.name !== "NotAllowedError")) {
            console.error("[passkey] startAuthentication failed:", err);
            capturePasskeyError(err, { step: "startAuthentication", hasCtxToken: !!ctxToken });
          }
          // SecurityError/ConstraintError/NotSupportedError mean a persistent rpID/origin
          // misconfiguration or authenticator capability mismatch — not a cancel. Retrying the
          // identical challenge will fail identically every time.
          if (isNonRetryablePasskeyError(err)) {
            setLoginStep("passkey-unsupported");

            return;
          }
          // Security: password fallback is NOT allowed when a passkey exists. Passkey is mandatory,
          // so a failed ceremony offers a RETRY of the same challenge — never a password/registration
          // fallback. Store options + ctx token so the retry button can re-trigger the same challenge
          // without a server round-trip. ctxToken is still valid in Redis (not consumed yet).
          if (ctxToken) {
            pendingPasskeyAuthRef.current = { options: fetcherData.passkeyOptions, ctxToken };
            setLoginStep("passkey-auth-cancelled");
          } else {
            // check-email flow with no ctx token — no password fallback either, go back to email
            setLoginStep("email");
          }
        });
    }

    // Device-key challenge: passkey already verified server-side; now prove WHICH browser profile
    // by signing a fresh server-issued nonce with the IndexedDB-stored private key.
    if (fetcherData.deviceKeyChallenge && fetcherData.challenge) {
      passkeyHandled.current = true;
      const ctx = fetcherData.loginContext || {};
      const ctxToken: string | undefined = ctx.token;
      const userId = fetcherData.userId as string | undefined;
      (async () => {
        try {
          const stored = userId ? await getStoredDeviceKey(userId) : null;
          if (!stored || !ctxToken) {
            toast.error("Device verification failed. Please try again.");
            passkeyHandled.current = false;

            return;
          }
          const signature = await signChallengeB64(stored.privateKey, fetcherData.challenge);
          // The server re-locates this device row from the public key on this same request (it no
          // longer binds to a stashed device id), so send BOTH the signature and the public key.
          const publicKeyB64 = await exportDevicePublicKeyB64(stored.publicKey);
          // Reset before submitting so this same effect processes the verify-device-key response
          // (signInServer redirect, or a devicePending/failure toast) on its next run.
          passkeyHandled.current = false;
          fetcher.submit(
            {
              loginContextToken: ctxToken,
              _action: "verify-device-key",
              deviceKeySignature: signature,
              devicePublicKey: publicKeyB64,
            },
            { method: "post" },
          );
        } catch (err) {
          console.error("[device-key] challenge signing failed:", err);
          console.error(err, { feature: "device-key-login" });
          toast.error("Device verification failed. Please try again.");
          passkeyHandled.current = false;
        }
      })();

      return;
    }

    // register-passkey succeeded; server wants a device key enrolled before it will create the
    // session (register-passkey's handler stopped short of signInServer for exactly this reason).
    if (fetcherData.deviceKeyEnrollRequired && fetcherData.loginContext?.token) {
      passkeyHandled.current = true;
      const ctxToken: string = fetcherData.loginContext.token;
      const userId = fetcherData.userId as string | undefined;
      (async () => {
        try {
          if (!userId) throw new Error("Missing userId for device-key enrollment");
          // Reuse an existing stored keypair if this browser profile already has one for this user
          // — deviceKeyEnrollRequired can fire again after the client already enrolled (e.g. the
          // server's success response was lost, or a transient mismatch), and unconditionally
          // generating a fresh keypair here would silently discard a still-valid private key,
          // permanently orphaning the old UserDevice row against the user's device limit (its
          // public key would no longer match anything the client can sign a challenge for).
          const keyPair = (await getStoredDeviceKey(userId).catch(() => null)) || (await generateAndStoreDeviceKey(userId));
          const publicKeyB64 = await exportDevicePublicKeyB64(keyPair.publicKey);
          // Reset before submitting so this same effect processes the enroll-device-key response
          // (signInServer redirect, or a devicePending/failure toast) on its next run.
          passkeyHandled.current = false;
          fetcher.submit(
            {
              loginContextToken: ctxToken,
              _action: "enroll-device-key",
              devicePublicKey: publicKeyB64,
            },
            { method: "post" },
          );
        } catch (err) {
          console.error("[device-key] enrollment failed:", err);
          console.error(err, { feature: "device-key-enroll" });
          toast.error("Could not set up this device. Please contact your administrator.");
          passkeyHandled.current = false;
          setPasskeyProcessing(false);
        }
      })();

      return;
    }

    // Passkey registration required (admin approved, first time on this device)
    if (fetcherData.passkeyRegistration && fetcherData.registrationOptions && typeof fetcherData.registrationOptions === "object") {
      passkeyHandled.current = true;
      setPasskeyProcessing(true);
      const ctx = fetcherData.loginContext || {};
      const ctxToken: string | undefined = ctx.token;
      const generation = ++passkeyGenerationRef.current;
      withPasskeyTimeout(startRegistration({ optionsJSON: fetcherData.registrationOptions }))
        .then((regResponse) => {
          if (generation !== passkeyGenerationRef.current) return;
          if (!ctxToken) {
            toast.error("Session expired. Please sign in again.");
            setPasskeyProcessing(false);
            passkeyHandled.current = false;
            setSelectedAccountEmail(null);

            return;
          }
          passkeyFlowComplete.current = true;
          // Reset before submitting so this same effect processes the register-passkey response
          // (typically a deviceKeyEnrollRequired follow-up) on its next run.
          passkeyHandled.current = false;
          fetcher.submit(
            {
              loginContextToken: ctxToken,
              _action: "register-passkey",
              passkeyResponse: JSON.stringify(regResponse),
            },
            { method: "post" },
          );
        })
        .catch((err: unknown) => {
          const isTimeout = err instanceof PasskeyTimeoutError;
          if (!isTimeout && generation !== passkeyGenerationRef.current) return;
          if (isTimeout) {
            passkeyGenerationRef.current += 1;
          }
          const isCancelled = err instanceof Error && err.name === "NotAllowedError";
          // InvalidStateError: the authenticator (e.g. iCloud Keychain) already holds a resident
          // credential for this exact (rpID, userHandle) pair — a per-spec dedup check the
          // authenticator enforces itself, independent of the server's excludeCredentials list.
          // Retrying the identical request can never succeed; no point offering "Try Again".
          const isConflict = err instanceof Error && err.name === "InvalidStateError";
          const isUnsupported = isNonRetryablePasskeyError(err);
          if (isTimeout) {
            toast.error("The biometric prompt took too long to respond. Please try again.");
          }
          if (!isCancelled && !isConflict && !isUnsupported && !isTimeout) {
            console.error("[passkey] startRegistration failed:", err);
          }
          if (isConflict || isUnsupported || (!isCancelled && !isConflict && !isUnsupported && !isTimeout)) {
            capturePasskeyError(err, { step: "startRegistration", hasCtxToken: !!ctxToken });
          }
          setPasskeyProcessing(false);
          if (isUnsupported) {
            pendingPasskeyRegRef.current = null;
            setLoginStep("passkey-unsupported");
            setSelectedAccountEmail(null);

            return;
          }
          if (isConflict) {
            pendingPasskeyRegRef.current = null;
            setLoginStep("passkey-register-conflict");
          } else if (ctxToken) {
            // Store options + ctx token for retry. loginContextToken is still valid in Redis
            // (startRegistration never consumes it — only the server-side register-passkey action does).
            pendingPasskeyRegRef.current = { options: fetcherData.registrationOptions, ctxToken };
            setLoginStep("passkey-register-cancelled");
          } else {
            setLoginStep("email");
          }
          setSelectedAccountEmail(null);
        });
    }
  }, [fetcher?.data]);

  // Reset passkey state only after a terminal verify/register round-trip completes
  useEffect(() => {
    if (fetcher.state === "idle" && passkeyProcessing && passkeyFlowComplete.current) {
      setPasskeyProcessing(false);
      passkeyHandled.current = false;
      passkeyFlowComplete.current = false;
    }
  }, [fetcher.state]);

  const prevResultErrorRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const errorMsg = (fetcher?.data as any)?.result?.errors?.message;
    if (errorMsg && errorMsg !== prevResultErrorRef.current) {
      prevResultErrorRef.current = errorMsg;
      toast.error(errorMsg);
    }
    if (!errorMsg) {
      prevResultErrorRef.current = undefined;
    }
  }, [(fetcher?.data as any)?.result?.errors?.message]);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    handleSubmit(e);
  };

  const resetPasskeyState = () => {
    pendingPasskeyAuthRef.current = null;
    pendingPasskeyRegRef.current = null;
    passkeyHandled.current = false;
    passkeyFlowComplete.current = false;
    knownUserIdRef.current = "";
    setStoredDevicePublicKey("");
    // Invalidate any in-flight ceremony (e.g. user clicked "Use a different account" while a
    // biometric prompt was still pending) so its eventual .then()/.catch() is a no-op.
    passkeyGenerationRef.current += 1;
    setPasskeyProcessing(false);
    setForceRegisterNewDevice(false);
  };

  // Retry passkey authentication without a server round-trip (options still valid in browser).
  // Passkey is mandatory when on — a failed ceremony re-offers the same challenge by default. The
  // only escape hatch is handleRegisterNewDevice (see below), which still forces password+2FA
  // before allowing a new credential to be registered for this device.
  const handleRetryPasskeyAuth = () => {
    const pending = pendingPasskeyAuthRef.current;
    if (!pending) return;
    passkeyHandled.current = false;
    setPasskeyProcessing(true);
    const generation = ++passkeyGenerationRef.current;
    withPasskeyTimeout(startAuthentication({ optionsJSON: pending.options }))
      .then((authResponse) => {
        if (generation !== passkeyGenerationRef.current) return;
        passkeyFlowComplete.current = true;
        // See the note in the primary passkeyAuth handler: attach the stored device key (if any)
        // so a returning device isn't mistaken for a new one on retry.
        (async () => {
          const userId = knownUserIdRef.current || undefined;
          const stored = userId ? await getStoredDeviceKey(userId).catch(() => null) : null;
          const devicePublicKey = stored ? await exportDevicePublicKeyB64(stored.publicKey).catch(() => undefined) : undefined;
          fetcher.submit(
            {
              loginContextToken: pending.ctxToken,
              _action: "verify-passkey",
              passkeyResponse: JSON.stringify(authResponse),
              ...(devicePublicKey ? { devicePublicKey } : {}),
            },
            { method: "post" },
          );
        })();
      })
      .catch((err: unknown) => {
        const isTimeout = err instanceof PasskeyTimeoutError;
        if (!isTimeout && generation !== passkeyGenerationRef.current) return;
        if (isTimeout) {
          passkeyGenerationRef.current += 1;
        }
        if (isTimeout) {
          toast.error("The biometric prompt took too long to respond. Please try again.");
        }
        if (!isTimeout && (!(err instanceof Error) || err.name !== "NotAllowedError")) {
          console.error("[passkey] retry startAuthentication failed:", err);
          capturePasskeyError(err, { step: "retryStartAuthentication", hasCtxToken: true });
        }
        if (isNonRetryablePasskeyError(err)) {
          pendingPasskeyAuthRef.current = null;
          setPasskeyProcessing(false);
          setLoginStep("passkey-unsupported");

          return;
        }
        // Passkey is mandatory — a cancel/timeout just re-offers the same challenge, forever if
        // needed. There is no password fallback: the pending options + ctxToken stay put.
        setPasskeyProcessing(false);
        setLoginStep("passkey-auth-cancelled");
      });
  };

  // Retry passkey registration without a server round-trip.
  const handleRetryPasskeyReg = () => {
    const pending = pendingPasskeyRegRef.current;
    if (!pending) return;
    passkeyHandled.current = false;
    setPasskeyProcessing(true);
    const generation = ++passkeyGenerationRef.current;
    withPasskeyTimeout(startRegistration({ optionsJSON: pending.options }))
      .then((regResponse) => {
        if (generation !== passkeyGenerationRef.current) return;
        passkeyFlowComplete.current = true;
        fetcher.submit(
          {
            loginContextToken: pending.ctxToken,
            _action: "register-passkey",
            passkeyResponse: JSON.stringify(regResponse),
          },
          { method: "post" },
        );
      })
      .catch((err: unknown) => {
        const isTimeout = err instanceof PasskeyTimeoutError;
        if (!isTimeout && generation !== passkeyGenerationRef.current) return;
        if (isTimeout) {
          passkeyGenerationRef.current += 1;
        }
        const isConflict = err instanceof Error && err.name === "InvalidStateError";
        const isUnsupported = isNonRetryablePasskeyError(err);
        if (isTimeout) {
          toast.error("The biometric prompt took too long to respond. Please try again.");
        }
        if (!isTimeout && (!(err instanceof Error) || err.name !== "NotAllowedError") && !isConflict && !isUnsupported) {
          console.error("[passkey] retry startRegistration failed:", err);
        }
        if (!isTimeout && (isConflict || isUnsupported || !(err instanceof Error) || err.name !== "NotAllowedError")) {
          capturePasskeyError(err, { step: "retryStartRegistration", hasCtxToken: true });
        }
        setPasskeyProcessing(false);
        if (isUnsupported) {
          pendingPasskeyRegRef.current = null;
          setLoginStep("passkey-unsupported");

          return;
        }
        if (isConflict) {
          pendingPasskeyRegRef.current = null;
          setLoginStep("passkey-register-conflict");

          return;
        }
        setLoginStep("passkey-register-cancelled");
      });
  };

  // This browser/device has no local WebAuthn credential for an account that already has a passkey
  // elsewhere (e.g. a second device that never enrolled, or a non-synced authenticator) — the auth
  // ceremony can never succeed here. Fall back to password+2FA, then force a NEW passkey
  // registration for this device once identity is proven fresh on this request (see
  // forceRegisterNewDevice in signInAction). This never invalidates the credential on the user's
  // other device — PasskeyUsers rows are additive, keyed on credentialId, not userId.
  const handleRegisterNewDevice = () => {
    setForceRegisterNewDevice(true);
    pendingPasskeyAuthRef.current = null;
    setPasskeyProcessing(false);
    passkeyHandled.current = false;
    passkeyFlowComplete.current = false;
    // Clear any stale 2FA token from a prior attempt so the OTP field isn't pre-filled with an
    // expired code on this fresh password step.
    setToken("");
    setLoginStep("password");
  };

  // Go back to email/accounts step
  const handleChangeEmail = () => {
    const isRecentEmail = recentAccounts.some((a) => a.email === emailValue);
    if (recentAccounts.length > 0 && isRecentEmail) {
      setLoginStep("accounts");
    } else {
      setLoginStep("email");
    }
    setEmailValue("");
    setValue("email", "");
    setSelectedAccountEmail(null);
    resetPasskeyState();
  };

  const handleSelectAccount = (account: RecentAccount) => {
    setSelectedAccountEmail(account.email);
    setEmailValue(account.email);
    setValue("email", account.email);
    resetPasskeyState();
    fetcher.submit(
      {
        email: account.email,
        _action: "check-email",
        deviceFingerPrint: deviceFingerPrint || "",
        devicePlatform: deviceInfo.platform,
        devicePlatformVersion: deviceInfo.platformVersion,
        deviceBrowserName: deviceInfo.browserName,
        deviceBrowserVersion: deviceInfo.browserVersion,
        deviceIsMobile: String(deviceInfo.isMobile),
      },
      { method: "post" },
    );
  };

  const handleRemoveAccount = (email: string) => {
    removeRecentAccount(email);
    const updated = recentAccounts.filter((a) => a.email !== email);
    setRecentAccounts(updated);
    if (selectedAccountEmail === email) {
      setSelectedAccountEmail(null);
      resetPasskeyState();
    }
    if (updated.length === 0) {
      setLoginStep("email");
    }
  };

  const handleUseAnotherAccount = () => {
    setLoginStep("email");
    setEmailValue("");
    setValue("email", "");
    setSelectedAccountEmail(null);
    resetPasskeyState();
  };

  return (
    <Page wrapper fetcher={fetcher}>
      <Flex direction="col" justifyContent="center" className="h-screen bg-blueshade">
        <Flex className="glass-effect bg-opacity-0 h-screen items-center">
          <div className="w-full flex flex-col justify-center items-center mx-6">
            <motion.div
              initial={{ scale: 0.95 }}
              whileInView={{
                scale: 1,
              }}
            >
              <Flex justifyContent="center">
                <AnimatedGradientText className="text-5xl mb-10">{brandName}</AnimatedGradientText>
              </Flex>
            </motion.div>
            <div className="rounded-lg bg-white/80 shadow-sm w-full px-2 md:px-5 md:w-[30rem] pt-5 pb-7 border border-primary-foreground">
              <CardTitle className="text-2xl">Login</CardTitle>
              <CardDescription>
                {loginStep === "accounts" ? (
                  "Choose an account to continue."
                ) : loginStep === "email" ? (
                  "Enter your email to continue."
                ) : (
                  <>
                    Signing in as <strong>{emailValue}</strong>{" "}
                    <button type="button" className="text-blue-500 hover:underline text-xs ml-1" onClick={handleChangeEmail}>
                      (change)
                    </button>
                  </>
                )}
              </CardDescription>

              <CardContent className="w-full p-0">
                {/* Step 0: Account picker */}
                {loginStep === "accounts" && (
                  <Flex direction="col" className="mx-auto w-full mt-4 space-y-2">
                    {recentAccounts.map((account) => {
                      const isSelected = selectedAccountEmail === account.email;
                      const isAccountLoading = isSelected && (isLoading || passkeyProcessing);

                      return (
                        <button
                          key={account.email}
                          type="button"
                          className={`relative flex items-center gap-3 w-full p-3 rounded-lg border transition-colors text-left group ${isAccountLoading ? "border-primary bg-primary/5" : "border-gray-200 hover:bg-gray-50"} ${
                            isLoading || passkeyProcessing ? "pointer-events-none opacity-60" : ""
                          } ${isAccountLoading ? "!opacity-100" : ""}`}
                          onClick={() => handleSelectAccount(account)}
                          disabled={isLoading || passkeyProcessing}
                        >
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg font-semibold ${isAccountLoading ? "animate-pulse" : ""}`}>{(account.name?.[0] || account.email[0]).toUpperCase()}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{account.name || account.email}</p>
                            {account.name && <p className="text-xs text-muted-foreground truncate">{account.email}</p>}
                            {isAccountLoading && <p className="text-xs text-primary animate-pulse mt-0.5">Signing in...</p>}
                          </div>
                          {!isAccountLoading && (
                            <button
                              type="button"
                              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveAccount(account.email);
                              }}
                            >
                              <Icon icon={faXmark} name="xmark-solid" className="h-3 w-3 text-muted-foreground hover:text-red-500" />
                            </button>
                          )}
                        </button>
                      );
                    })}
                    {passkeyProcessing && (
                      <FlexItem className="w-full text-center py-2">
                        <p className="text-sm text-muted-foreground animate-pulse">Verifying your device...</p>
                      </FlexItem>
                    )}
                    <Button type="button" variant="outline" className="w-full mt-2 h-10 text-sm" onClick={handleUseAnotherAccount} disabled={passkeyProcessing}>
                      Use another account
                    </Button>
                  </Flex>
                )}

                {/* Step 1: Email only */}
                {loginStep === "email" && (
                  <Form onSubmit={handleEmailCheck}>
                    <Flex direction="col" alignItems="center" justifyContent="center" className="mx-auto w-full mt-4 space-y-3">
                      <FlexItem className="w-full">
                        <Label className="text-md" htmlFor="email">
                          Email
                        </Label>
                        <Input placeholder="email" data-testid={TEST_IDS.loginEmailInput} {...register("email")} error={errors?.email?.message?.toString()} className="text-md h-10 py-2" />
                      </FlexItem>
                      {passkeyProcessing && (
                        <FlexItem className="w-full text-center py-2">
                          <p className="text-sm text-muted-foreground animate-pulse">Verifying your device...</p>
                        </FlexItem>
                      )}
                      <Button type="submit" data-testid={TEST_IDS.loginContinueBtn} className="px-4 py-2 h-10 text-lg" full disabled={isLoading || passkeyProcessing} isLoading={isLoading || passkeyProcessing} onClick={() => toast.dismiss()} variant="default">
                        {passkeyProcessing ? "Verifying Device..." : "Continue"}
                      </Button>
                    </Flex>
                  </Form>
                )}

                {/* Step 2: Password + 2FA (no active passkey found) */}
                {loginStep === "password" && (
                  <Form onSubmit={handlePasswordSubmit}>
                    <Flex direction="col" alignItems="center" justifyContent="center" className="mx-auto w-full mt-4 space-y-3">
                      <FlexItem className="w-full">
                        <Label htmlFor="password" className="text-md">
                          Password
                        </Label>
                        <div className="relative">
                          <Input
                            type={passwordVisible ? "text" : "password"}
                            placeholder="password"
                            data-testid={TEST_IDS.loginPasswordInput}
                            {...register("password")}
                            error={errors?.password?.message?.toString()}
                            suffixIcon={
                              <Flex alignItems="center" justifyContent="end" className="absolute inset-y-0 right-2 w-auto">
                                <Icon icon={passwordVisible ? faEyeSlash : faEye} name={passwordVisible ? "eye-slash-solid" : "eye-solid"} className="cursor-pointer" onClick={() => setPasswordVisible(!passwordVisible)} />
                              </Flex>
                            }
                            className="text-md h-10 py-2"
                          />
                        </div>
                      </FlexItem>
                      {!bypass2fa && (
                        <FlexItem className="w-full">
                          <Flex direction="col" alignItems="center" className="my-2">
                            <FlexItem>
                              <Label className="text-lg font-semibold">2FA Token</Label>
                            </FlexItem>
                            <FlexItem>
                              <InputOTP maxLength={6} onChange={setToken}>
                                <InputOTPGroup>
                                  <InputOTPSlot index={0} className="h-12 text-lg" />
                                  <InputOTPSlot index={1} className="h-12 text-lg" />
                                  <InputOTPSlot index={2} className="h-12 text-lg" />
                                </InputOTPGroup>
                                <InputOTPSeparator />
                                <InputOTPGroup>
                                  <InputOTPSlot index={3} className="h-12 text-lg" />
                                  <InputOTPSlot index={4} className="h-12 text-lg" />
                                  <InputOTPSlot index={5} className="h-12 text-lg" />
                                </InputOTPGroup>
                              </InputOTP>
                            </FlexItem>
                            <FlexItem className="w-full my-2">
                              <Flex justifyContent="center" alignItems="center" className="text-sm">
                                <FlexItem>
                                  <span>Don't have 2FA token?</span>
                                </FlexItem>
                                <FlexItem>
                                  <Link to="/set2fa" className="text-blue-500 hover:underline cursor-pointer">
                                    click here
                                  </Link>
                                </FlexItem>
                              </Flex>
                            </FlexItem>
                          </Flex>
                        </FlexItem>
                      )}
                      {passkeyProcessing && (
                        <FlexItem className="w-full text-center py-2">
                          <p className="text-sm text-muted-foreground animate-pulse">Setting up your device... Please complete the biometric prompt.</p>
                        </FlexItem>
                      )}
                      <Button type="submit" data-testid={TEST_IDS.loginSignInBtn} className="px-4 py-2 h-10 text-lg" full disabled={isLoading || passkeyProcessing} isLoading={isLoading || passkeyProcessing} onClick={() => toast.dismiss()} variant="default">
                        {passkeyProcessing ? "Setting up Device..." : "Sign in"}
                      </Button>
                    </Flex>
                  </Form>
                )}

                {/* Passkey auth cancelled — retry required, no password fallback */}
                {loginStep === "passkey-auth-cancelled" && (
                  <Flex direction="col" alignItems="center" justifyContent="center" className="mx-auto w-full mt-4 space-y-3">
                    <FlexItem className="w-full">
                      <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                        <p className="font-semibold mb-1">Passkey verification required</p>
                        <p>Your account requires passkey authentication. Please complete the biometric verification to continue.</p>
                      </div>
                    </FlexItem>
                    <Button type="button" className="px-4 py-2 h-10 text-lg" full disabled={passkeyProcessing} isLoading={passkeyProcessing} onClick={handleRetryPasskeyAuth} variant="default">
                      {passkeyProcessing ? "Verifying..." : "Try Again"}
                    </Button>
                    <Button type="button" variant="outline" className="w-full h-10 text-sm" onClick={handleRegisterNewDevice} disabled={passkeyProcessing}>
                      Register a new passkey on this device
                    </Button>
                    <Button type="button" variant="outline" className="w-full h-10 text-sm" onClick={handleChangeEmail} disabled={passkeyProcessing}>
                      Use a different account
                    </Button>
                  </Flex>
                )}

                {/* Passkey registration cancelled — retry required */}
                {loginStep === "passkey-register-cancelled" && (
                  <Flex direction="col" alignItems="center" justifyContent="center" className="mx-auto w-full mt-4 space-y-3">
                    <FlexItem className="w-full">
                      <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                        <p className="font-semibold mb-1">Passkey setup required</p>
                        <p>You need to register a passkey to access your account. Please complete the biometric setup to continue.</p>
                      </div>
                    </FlexItem>
                    <Button type="button" className="px-4 py-2 h-10 text-lg" full disabled={passkeyProcessing} isLoading={passkeyProcessing} onClick={handleRetryPasskeyReg} variant="default">
                      {passkeyProcessing ? "Setting up..." : "Set Up Passkey"}
                    </Button>
                    <Button type="button" variant="outline" className="w-full h-10 text-sm" onClick={handleChangeEmail} disabled={passkeyProcessing}>
                      Use a different account
                    </Button>
                  </Flex>
                )}

                {/* Passkey registration conflict — this authenticator already holds a resident
                    credential for this account (e.g. stale iCloud Keychain entry); retrying the
                    same request can never succeed, so no "Try Again" is offered here. */}
                {loginStep === "passkey-register-conflict" && (
                  <Flex direction="col" alignItems="center" justifyContent="center" className="mx-auto w-full mt-4 space-y-3">
                    <FlexItem className="w-full">
                      <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                        <p className="font-semibold mb-1">Passkey already exists on this device</p>
                        <p>This browser or device already has a passkey saved for this account, but it's no longer recognized by our system. Remove it from your browser or device's passkey settings (e.g. iCloud Keychain, Chrome Passwords), then sign in again.</p>
                      </div>
                    </FlexItem>
                    <Button type="button" variant="outline" className="w-full h-10 text-sm" onClick={handleChangeEmail} disabled={passkeyProcessing}>
                      Use a different account
                    </Button>
                  </Flex>
                )}

                {/* Passkey unsupported — SecurityError/ConstraintError/NotSupportedError means a
                    persistent rpID/origin misconfiguration or authenticator capability mismatch,
                    not a cancel. Retrying the identical request will fail identically every time. */}
                {loginStep === "passkey-unsupported" && (
                  <Flex direction="col" alignItems="center" justifyContent="center" className="mx-auto w-full mt-4 space-y-3">
                    <FlexItem className="w-full">
                      <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                        <p className="font-semibold mb-1">Passkey not supported on this device</p>
                        <p>This browser or device can't complete passkey setup for your account. Try a different browser or device, or contact your administrator for help.</p>
                      </div>
                    </FlexItem>
                    <Button type="button" variant="outline" className="w-full h-10 text-sm" onClick={handleChangeEmail} disabled={passkeyProcessing}>
                      Use a different account
                    </Button>
                  </Flex>
                )}

                {/* Device pending — a brand-new device awaiting admin approval. Terminal state:
                    no retry loop (an admin must act), so only offer a route back to the email step. */}
                {loginStep === "device-pending" && (
                  <Flex direction="col" alignItems="center" justifyContent="center" className="mx-auto w-full mt-4 space-y-3">
                    <FlexItem className="w-full">
                      <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                        <p className="font-semibold mb-1">Device pending approval</p>
                        <p>Your device is pending admin approval — please contact your administrator. You can sign in once your device has been approved.</p>
                      </div>
                    </FlexItem>
                    <Button type="button" variant="outline" className="w-full h-10 text-sm" onClick={handleChangeEmail} disabled={passkeyProcessing}>
                      Back to sign in
                    </Button>
                  </Flex>
                )}

                {/* Device revoked/rejected — an admin explicitly blocked this device. Terminal
                    state: no retry loop (the passkey ceremony would just succeed and hit this gate
                    again), so only offer a route back to the email step. Message is deliberately
                    generic — never reveal to the end user which admin action (revoke/reject/reset)
                    was taken. */}
                {loginStep === "device-revoked" && (
                  <Flex direction="col" alignItems="center" justifyContent="center" className="mx-auto w-full mt-4 space-y-3">
                    <FlexItem className="w-full">
                      <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                        <p className="font-semibold mb-1">Unable to sign in</p>
                        <p>You're unable to sign in right now. Please contact your administrator.</p>
                      </div>
                    </FlexItem>
                    <Button type="button" variant="outline" className="w-full h-10 text-sm" onClick={handleChangeEmail} disabled={passkeyProcessing}>
                      Back to sign in
                    </Button>
                  </Flex>
                )}

                {/* Device limit reached — this account already has the maximum number of devices
                    registered. No admin action was taken against this device specifically; every
                    slot is simply occupied, so a retry from this same device can never succeed
                    until an admin frees a slot. */}
                {loginStep === "device-limit" && (
                  <Flex direction="col" alignItems="center" justifyContent="center" className="mx-auto w-full mt-4 space-y-3">
                    <FlexItem className="w-full">
                      <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                        <p className="font-semibold mb-1">Device limit reached</p>
                        <p>You've reached the maximum number of devices allowed for this account. Please contact your administrator to remove an existing device before signing in on a new one.</p>
                      </div>
                    </FlexItem>
                    <Button type="button" variant="outline" className="w-full h-10 text-sm" onClick={handleChangeEmail} disabled={passkeyProcessing}>
                      Back to sign in
                    </Button>
                  </Flex>
                )}
              </CardContent>
            </div>
          </div>
        </Flex>
      </Flex>
    </Page>
  );
};

export default Login;

export const ErrorBoundary = () => <ErrorComponent />;
